import type { MiddlewareAPI } from 'redux';
import { batchActions, type BatchAction } from 'redux-batched-actions';
import type { ActionsObservable } from 'redux-observable';
import find from 'lodash/find';
import { Observable } from 'rxjs/Observable';
import {
	unstable_scheduleCallback as scheduleCallback,
	unstable_IdlePriority as idlePriority,
} from 'scheduler';
import fireErrorAnalytics, {
	getStatusCodeGroup,
} from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import type { ErrorResponse } from '@atlassian/jira-servicedesk-common/src/utils/fetch/index.tsx';
import 'rxjs/add/observable/of';
import type { Column } from '@atlassian/jira-servicedesk-queues-common/src/model/index.tsx';
import { withConcurrencyLimiting } from '../../../common/rxjs-util/concurrency-limiter/index.tsx';
import {
	type LoadIssuesActionSource,
	LOAD_ISSUES_ACTION_SOURCE_FILTER,
	LOAD_ISSUES_ACTION_SOURCE_PAGE,
	LOAD_ISSUES_ACTION_SOURCE_POLL,
	LOAD_ISSUES_ACTION_SOURCE_VIEW,
	LOAD_ISSUES_ACTION_SOURCE_SORT,
	LOAD_ISSUES_ACTION_SOURCE_SCROLL,
} from '../../../model/index.tsx';

import getIssueData from '../../../services/issue/get/index.tsx';
import { filterBadQueryAction } from '../../../state/actions/filter/index.tsx';
import {
	LOAD_ISSUES_INTERNAL,
	loadIssuesFailureAction,
	loadIssuesSuccessAction,
} from '../../../state/actions/issue/index.tsx';
import {
	sortOrderOnLoadAction,
	updateSortingInfoInUserPropertyAction,
} from '../../../state/actions/table/index.tsx';
import type { Action } from '../../../state/actions/types.tsx';
import { restartPollAction } from '../../../state/actions/update-metadata/index.tsx';
import type { ManualSorting } from '../../../state/reducers/persisted/queue/types.tsx';
import { unsetIsInitialLoad } from '../../../state/reducers/persisted/ssr-issues-data/index.tsx';
import type { State } from '../../../state/reducers/types.tsx';
import { getIsNewFilterQueryPending } from '../../../state/selectors/filter/index.tsx';
import { getIssueCacheSize } from '../../../state/selectors/issues/index.tsx';
import { getQueueId } from '../../../state/selectors/queue/index.tsx';
import { getIsInitialLoad } from '../../../state/selectors/ssr/index.tsx';

const MAX_TOTAL_COUNT_DIFF = 30;

const getManualSortFromIssueListResult = (
	columns: Column[],
	isUsingDefaultSorting: boolean,
): ManualSorting | undefined => {
	const manualSortingColumn = isUsingDefaultSorting
		? undefined
		: find(columns, (column) => column.currentSorting != null);

	// @ts-expect-error - TS2322 - Type '{ sortedBy: string; sortOrder: SortOrder | undefined; } | undefined' is not assignable to type 'ManualSorting | undefined'.
	return manualSortingColumn
		? {
				sortedBy: manualSortingColumn.id,
				sortOrder: manualSortingColumn.currentSorting,
			}
		: undefined;
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<Action | BatchAction>, store: MiddlewareAPI<State>) =>
	withConcurrencyLimiting(action$.ofType(LOAD_ISSUES_INTERNAL), 3, (action) => {
		const state: State = store.getState();
		const queueId = getQueueId(state);

		const shouldFireGenericErrorAnalytics = (error: ErrorResponse) => {
			if (
				getStatusCodeGroup(new FetchError(error.statusCode)) === 'unknown' ||
				isClientFetchError(error)
			) {
				return false;
			}
			return true;
		};

		const sendGenericErrorAnalytics = (error: ErrorResponse) => {
			fireErrorAnalytics({
				meta: {
					id: 'jsmIssueListLoading',
					packageName: 'jiraServicedeskQueuesAgentView',
					teamName: 'jsd-shield',
				},
				sendToPrivacyUnsafeSplunk: true,
				error: new FetchError(error.statusCode),
				attributes: {
					reasonKey: error.response.reasonKey,
					reasonCode: error.response.reasonCode,
				},
			});
		};

		const sendGenericErrorAnalyticsConditionally = (error: ErrorResponse) => {
			if (shouldFireGenericErrorAnalytics(error)) {
				sendGenericErrorAnalytics(error);
			}
		};

		const sendSearchErrorAnalytics = (error: ErrorResponse) => {
			fireErrorAnalytics({
				meta: {
					id: 'jsmIssueListLoadingFilter',
					packageName: 'jiraServicedeskQueuesAgentView',
					teamName: 'jsd-shield',
				},
				sendToPrivacyUnsafeSplunk: true,
				error: new FetchError(error.statusCode),
				attributes: {
					hasPassed: false,
				},
			});
		};

		const shouldFireSearchErrorAnalytics = (error: ErrorResponse): boolean => {
			const statusCodeGroup = getStatusCodeGroup(new FetchError(error.statusCode));
			return (
				action.payload.source === LOAD_ISSUES_ACTION_SOURCE_FILTER &&
				(statusCodeGroup === '5xx' || statusCodeGroup === '4xx') &&
				!isClientFetchError(error)
			);
		};

		const sendErrorAnalytics = (error: ErrorResponse) => {
			if (shouldFireSearchErrorAnalytics(error)) {
				sendSearchErrorAnalytics(error);
			}

			sendGenericErrorAnalyticsConditionally(error);
		};

		const isUpdate = [LOAD_ISSUES_ACTION_SOURCE_POLL, LOAD_ISSUES_ACTION_SOURCE_VIEW].includes(
			// @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'Action'.
			action.payload.source,
		);

		const isQueueFiltering = action.payload.source === LOAD_ISSUES_ACTION_SOURCE_FILTER;
		const isPageLoad = action.payload.source === LOAD_ISSUES_ACTION_SOURCE_PAGE;
		const isInitialLoad = getIsInitialLoad(state);

		const dispatchRestartPollActionWhenAppIdleInInitialLoad = () => {
			// only run this in initial load
			if (
				isPageLoad &&
				action.payload.startIndex === 0 &&
				!isUpdate &&
				!isQueueFiltering &&
				isInitialLoad
			) {
				scheduleCallback(idlePriority, () => {
					// @ts-expect-error - TS2345 - Argument of type 'RestartPollAction' is not assignable to parameter of type 'State'. Type 'RestartPollAction' is missing the following properties from type 'State': appProps, ui, persisted
					store.dispatch(restartPollAction(true));
				});
			}
		};
		// @ts-expect-error - TS2345 - rgument of type 'UnsetIsInitialLoad' is not assignable to parameter of type 'State'. Type 'UnsetIsInitialLoad' is missing the following properties from type 'State': appProps, ui, persisted
		const dispatchUnsetIsInitialLoadAction = () => store.dispatch(unsetIsInitialLoad());

		return (
			getIssueData(
				action.payload.startIndex,
				action.payload.endIndex,
				isUpdate,
				action.payload.source === LOAD_ISSUES_ACTION_SOURCE_PAGE,
				action.payload.bypassCache,
				state,
				action.payload.source === LOAD_ISSUES_ACTION_SOURCE_FILTER,
				dispatchUnsetIsInitialLoadAction,
				action.payload.source === LOAD_ISSUES_ACTION_SOURCE_SORT,
			)
				.flatMap((result) => {
					// dispatch only in initial load
					// running the blow will start the first poll as soon as TTAI finishes.
					if (fg('issuelist_skip_fetch_cache_hit')) {
						dispatchRestartPollActionWhenAppIdleInInitialLoad();
					}

					if (
						isUpdate &&
						result.totalCount - state.persisted.issues.totalCount > MAX_TOTAL_COUNT_DIFF &&
						fg('issue_list_total_counts_mismatch')
					) {
						// we're temporarily logging mismatches between total count from /issuelist (JSIS) and /issuelist/updated (JVIS) to look for any bugs
						fireErrorAnalytics({
							meta: {
								id: 'jsmIssueListTotalCountMismatch',
								packageName: 'jiraServicedeskQueuesAgentView',
								teamName: 'jsd-shield',
							},
							sendToPrivacyUnsafeSplunk: true,
							error: new Error('total count big mismatch'),
							attributes: {
								diff: result.totalCount - state.persisted.issues.totalCount,
							},
							skipSentry: true,
						});
					}

					const actions = [
						restartPollAction(),
						loadIssuesSuccessAction(
							result,
							queueId,
							action.payload.startIndex,
							action.payload.source,
						),
					];

					const sortingMigrationExcludedSources: LoadIssuesActionSource[] = [
						LOAD_ISSUES_ACTION_SOURCE_POLL,
						LOAD_ISSUES_ACTION_SOURCE_SCROLL,
						LOAD_ISSUES_ACTION_SOURCE_SORT,
						LOAD_ISSUES_ACTION_SOURCE_FILTER,
					];

					const shouldMigrateSorting = !sortingMigrationExcludedSources.includes(
						action.payload.source,
					);

					const manualSort = getManualSortFromIssueListResult(
						result.columns,
						result.isUsingDefaultSorting,
					);

					if (
						shouldMigrateSorting &&
						manualSort &&
						!fg('clean_local_storage_sorting') &&
						fg('migrate_sorting_to_user_property')
					) {
						actions.push(
							// @ts-expect-error - TS2322
							updateSortingInfoInUserPropertyAction(manualSort.sortedBy, manualSort.sortOrder),
						);
					}

					if (isInitialLoad && manualSort && fg('clean_local_storage_sorting')) {
						// @ts-expect-error - TS2322
						actions.push(sortOrderOnLoadAction(manualSort.sortedBy, manualSort.sortOrder));
					}

					return Observable.of(batchActions(actions));
				})
				// @ts-expect-error - TS2345 - Argument of type '(error: ErrorResponse) => Observable<BatchAction> | Observable<RestartPollAction>' is not assignable to parameter of type '(err: any, caught: Observable<BatchAction>) => ObservableInput<BatchAction>'.
				.catch((error: ErrorResponse) => {
					if (error.statusCode === 304) {
						return Observable.of(restartPollAction());
					}
					if (
						// Bad query request
						error.statusCode === 400 &&
						action.payload.source === LOAD_ISSUES_ACTION_SOURCE_FILTER
					) {
						const isNewFilterQueryPendingAction = getIsNewFilterQueryPending(state);

						return Observable.of(
							batchActions([
								restartPollAction(),
								filterBadQueryAction(isNewFilterQueryPendingAction),
							]),
						);
					}
					sendErrorAnalytics(error);

					return Observable.of(
						batchActions([
							restartPollAction(),
							loadIssuesFailureAction(
								action.payload.source,
								error.response,
								error.statusCode,
								getIssueCacheSize(state) === 0,
								error.traceId,
							),
						]),
					);
				})
		);
	});
